<template lang="pug">
  v-dialog(v-model="show" max-width="600px" scrollable :fullscreen="$vuetify.breakpoint.smAndDown" persistent)
    v-snackbar( v-model="snackbar" :color="snackbarColor" ) {{snackBarText}}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="snackbar = false")
          v-icon fal fa-times
    v-snackbar(v-model="showError" color="error")
      span {{ errorMessage }}
      v-btn(dark text @click="showError = false")
        v-icon fal fa-times
    v-card.fill-height
      v-card-title.ma-0.pa-0
        v-toolbar(flat dark color="primary")
          v-toolbar-title {{$t('profileDialog.dialogTitle')}}
          v-spacer
          v-btn(icon dark @click="onCloseButtonClick" :disabled="isSaveInProgress")
            v-icon fal fa-times
        v-skeleton-loader.sloader(v-if="showSkeletonLoader" type="list-item-two-line@3")
        template(v-else)
          v-tabs(show-arrows prev-icon="far fa-arrow-left" next-icon="far fa-arrow-right" v-model="tab")
            v-tab {{$t('profileDialog.ProfileTitle')}}
            v-tab {{$t('profileDialog.credentialsTitle')}}
      v-card-text(style="height: 50vh;")
        v-tabs-items(v-model="tab")
          v-tab-item
            v-card(flat)
              v-card-text.pt-6.pa-0
                v-form(ref="form" v-model="isFormValid" :lazy-validation="true" :disabled="isSaveInProgress" @submit.prevent )
                  v-row.ma-0
                    v-col.pa-0(cols="12")
                      v-select(:label="`${$t('languages.language')}`"
                          item-value="code"
                          item-text="label"
                          :items="supportedLanguages"
                          v-model="selectedItem"
                          outlined
                          return-object
                          append-icon="fal fa-chevron-down"
                          @change="onLanguageSelect(selectedItem)"
                          )
                  v-row.ma-0
                    v-col.py-0.pa-0(cols="12" md="6")
                      v-text-field(v-model="firstName" :label="` ${$t('profileDialog.firstName')}`" :rules="firstNameRules" outlined type="text" clearable)
                    v-col.py-0.pa-0.pl-md-2(cols="12" md="6")
                      v-text-field(v-model="familyName" :label="` ${$t('profileDialog.familyName')}`" :rules="familyNameRules" outlined)
                  v-row.ma-0
                    v-col.py-0.pa-0(cols="12" md="6")
                      v-text-field(v-model="phoneNumber" :label="` ${$t('profileDialog.phoneNumber')}`" outlined hide-details :rules="validatePhoneNumber")
                  v-list.pa-0
                    v-subheader {{$t('profileDialog.email')}}
                    v-list-item(v-for="(item, idx) in emailList" :key="idx")
                      v-list-item-content.pa-0
                        v-list-item-title(v-if="!item.isEdit") {{ item.email }}
                        v-text-field(v-if="item.isEdit" v-model="item.email" :rules="emailRules" autofocus  @keyup.enter="onSaveChangesClick(idx, item)" )
                      v-list-item-action
                        v-row.ma-0.pa-0(v-if="item.isEdit" align="end")
                          v-col.pa-0
                            v-btn(fab text x-small icon @click="onSaveChangesClick(idx, item)" :disabled="!isFormValid")
                              v-icon fa-check
                          v-col.pa-0
                            v-btn(fab text x-small icon @click="onCancelChangesClick(idx, item)")
                              v-icon fa-times
                        v-row.ma-0.pa-0(v-if="!item.isEdit")
                          v-col.pa-0
                            v-btn(fab text x-small icon @click="onEditClick(idx, item)" :disabled="isSaveInProgress || isInEditMode")
                              v-icon fa-pen
                          v-col.pa-0
                            v-btn(fab text x-small icon @click="onDeleteClick(idx)" :disabled="isSaveInProgress")
                              v-icon fa-trash
                v-row.ma-0
                  v-col.text-start(cols="12")
                    v-btn(@click="onAddButtonClick" :disabled="!isFormValid || isInEditMode || isSaveInProgress || showSkeletonLoader " ) {{$t('profileDialog.addEmailLabel')}}
                v-card-actions
                  v-spacer
                  v-col.text-end.pr-0.pa-0(cols="12")
                    v-btn(color="primary"
                      :disabled="!isFormValid || isInEditMode || showSkeletonLoader"
                      :loading="isSaveInProgress"
                      @click="onSubmitButtonClick") {{$t('profileDialog.saveButtonLabel')}}
          v-tab-item
            v-card(flat)
              v-form(v-model="formIsValid" ref="form" @submit.prevent="onSubmitCredentialsButtonClick" :disabled="saveInProgress")
                v-card-text.pa-0
                  v-row.ma-0
                    v-col.pb-0.pa-0.pt-4(cols="12")
                      v-text-field(outlined :label="$t('loginPage.userNameFieldLabel')" v-model="loginName" disabled)
                    v-col.pt-0.pa-0(cols="12" sm="12" md="7" lg="7")
                      v-text-field(outlined :label="$t('passwordRecovery.request.newPasswordFieldLabel')" v-model="password" :rules="passwordRules")
                    v-col.pt-3.pl-7(cols="4" v-if="this.$vuetify.breakpoint.mdAndUp")
                      v-btn(@click="generateCodeClick" medium :disabled="saveInProgress") {{$t('profileDialog.generatePassword')}}
                    v-col.text-center(cols="12" v-else)
                      v-btn(@click="generateCodeClick" medium :disabled="saveInProgress") {{$t('profileDialog.generatePassword')}}

                v-card-actions.pr-0
                  v-spacer
                  v-col.text-end.pr-0.pa-0(cols="12" sm="12" md="6" lg="6")
                    v-btn(color="primary" type="submit"  :disabled="!formIsValid" :loading="saveInProgress") {{$t('profileDialog.saveButtonLabel')}}

      v-divider
      v-card-actions
        v-spacer
        v-btn(@click="onCancelButtonClick" :disabled="isSaveInProgress || saveInProgress" text color="primary") {{$t('buttons.closeButtonLabel')}}
</template>

<script lang="ts">
import Vue from 'vue'
import {Action, Getter} from 'vuex-class'
import {CURRENT_UI_LANGUAGE, SUPPORTED_LANGUAGES, USER_LOGGED_IN} from '@/store/getter-types'
import {Component, Prop} from 'vue-property-decorator'
import {TranslateResult} from 'vue-i18n'
import i18n from '@/plugins/i18n'
import { AccountEmail } from '@/model/AccountEmail'
import {CurrentUser} from "@/model/CurrentUser";
import {
    FETCH_ACCOUNT_EMAILS,
    FETCH_ACCOUNT_USER, SWITCH_APPLICATION_LANGUAGE,
    UPDATE_ACCOUNT_EMAILS,
    UPDATE_ACCOUNT_USER, UPDATE_PERSON_CREDENTIAL_PASS,
} from "@/store/action-types";
import {ChangeNewPasswordPayload, UpdateAccountEmail, UserPayload} from "@/types/payload";
import _ from 'lodash'
import Language from "@/model/Language";

function valueIsRequired (v: string): boolean | TranslateResult {
  return !!v || i18n.t('validation.valueIsRequired')
}

function validateEmail (v: string): boolean | TranslateResult {
  if (v) {
    return /.+@.+\..+/.test(v) || i18n.t('validation.emailMustBeValid')
  }
  return true
}

function validatePhoneNumber (v: string): boolean | TranslateResult {
  if (v) {
    return  /^[+]?[\d\s]+$/.test(v) || i18n.t('validation.phoneNumberMustBeValid')
  }
  return true
}

function passwordLength(v: string): boolean | TranslateResult {
  if (v) {
    return v.length >= 4 || i18n.t('validation.passwordMinimumLength')
  }

  return false
}

@Component({
    components: {
    }
})
export default class ProfileDialog extends Vue {

  $refs!: {
    form: Vue & { validate: () => boolean }
  }

  @Prop({type: Boolean, default: false}) show!: boolean
  @Getter(SUPPORTED_LANGUAGES) supportedLanguages!: Language[]
  @Getter(CURRENT_UI_LANGUAGE) currentLanguage!: Language
  @Action(SWITCH_APPLICATION_LANGUAGE) switchApplicationLanguage!: { (payload: Language): void }


    private originalEmailList: AccountEmail[] = []
  private emailList: AccountEmail[] = []
  private passwordRules: Array<(v: string) => string | boolean | TranslateResult> = [
    (v: string) => !!v ||` i18n.t('validation.valueIsRequired')`,
    (v: string) => !!v ||` i18n.t('validation.valueIsRequiredMessage')`,
    (v: string) => (v && v.length > 3)  ||` i18n.t('validation.passwordMinimumLength')`,
    (v: string) => {
      const pattern = /^[a-zA-Z0-9]*$/

      return pattern.test(v) || `i18n.t('validation.valueIsRequiredMessage')`
    },
  ]
  private emailRules = [
    valueIsRequired,
    validateEmail,
  ]
  private firstNameRules = [
    valueIsRequired,
  ]
  private familyNameRules = [
    valueIsRequired,
  ]
  private validatePhoneNumber = [
    validatePhoneNumber
  ]
  private isFormValid: boolean = false
  private isSaveInProgress: boolean = false
  private isLoading: boolean = false
  private showError: boolean = false
  private errorMessage: TranslateResult | null = null
  private firstName: string = ''
  private familyName: string = ''
  private phoneNumber: string = ''
  private loginName: string = ''
  private password: string | null = null
  private formIsValid: boolean = false
  private saveInProgress: boolean = false
  private snackbar: boolean = false
  private snackBarText: string = ''
  private snackbarColor: string = ''
  private tab: number = 0
  private selectedItem: Language | null = null


  @Getter(USER_LOGGED_IN) currentUser!: CurrentUser
  @Action(UPDATE_PERSON_CREDENTIAL_PASS) updatePersonCredentialPassById!: { (payload: ChangeNewPasswordPayload): Promise<string> }
  @Action(UPDATE_ACCOUNT_USER) updateAccountUser!: ({ (payload: UserPayload): Promise<void> })
  @Action(UPDATE_ACCOUNT_EMAILS) updateAccountEmails!: ({ (payload: UpdateAccountEmail[]): Promise<AccountEmail[]> })
  @Action(FETCH_ACCOUNT_USER) fetchAccountUser!: ({ (): Promise<void> })
  @Action(FETCH_ACCOUNT_EMAILS) fetchAccountEmails!: ({ (): Promise<AccountEmail[]> })

  get isInEditMode (): boolean {
    return !!this.emailList.find((item: AccountEmail) => item.isEdit)
  }

  get showSkeletonLoader (): boolean {
    return this.isLoading
  }

  generateCodeClick () {
    this.password = Math.floor(1000 + Math.random() * 9000).toString()
  }

  onEditClick (idx: number, item: AccountEmail): void {
    this.emailList.splice(idx, 1, item.update!(!item.isEdit))
  }

  onAddButtonClick (): void {
    this.emailList.unshift(AccountEmail.createEmpty())
    this.originalEmailList.unshift(AccountEmail.createEmpty())
  }

  onSaveChangesClick (idx: number, item: AccountEmail): void {
    this.$refs.form.validate()
    if(this.isFormValid && item.email) {
      this.emailList.splice(idx, 1, item.update!(!item.isEdit))
      this.originalEmailList.splice(idx, 1, item.update!(!item.isEdit))
    }
  }

  onCloseButtonClick () {
    this.$emit('close')
  }

  onCancelButtonClick () {
    this.$emit('close')
  }

  onSubmitButtonClick () {
    this.save()
  }

  onSubmitCredentialsButtonClick () {
    this.updatePersonCredentialPassword()

  }

    onLanguageSelect (language: Language): void {
        this.switchApplicationLanguage(language)
        localStorage.setItem('uiLanguageCodeCp', language.code)
    }

  onDeleteClick (idx: number): void {
    this.emailList.splice(idx, 1)
    this.originalEmailList.splice(idx, 1)
  }



  onCancelChangesClick (idx: number, item: AccountEmail): void {
    if(this.originalEmailList[idx].email) {
      this.emailList.splice(idx, 1, this.originalEmailList[idx])
    } else {
      this.emailList.splice(idx, 1)
      this.originalEmailList.splice(idx, 1)
    }
  }

  async save(): Promise<void> {
    const payload: UpdateAccountEmail[] =  this.emailList.map((item: AccountEmail) => {
      return {email: item.email}
    })

    const userPayload: UserPayload = {
      firstName: this.firstName,
      familyName: this.familyName,
      phoneNumber: this.phoneNumber
    }
    try {
      this.isSaveInProgress = true
      await this.updateAccountUser(userPayload)
      this.emailList = await this.updateAccountEmails(payload)
      await this.fetchAccountUser()
    } catch (e) {
      this.errorMessage = this.$t('profileDialog.saveErrorMessage')
      this.showError = true
    } finally {
      this.isSaveInProgress = false
    }
  }

  async fetchData (): Promise<void> {
    const data: AccountEmail[] = await this.fetchAccountEmails()
    this.originalEmailList = _.cloneDeep(data)
    this.emailList = _.cloneDeep(data)
    this.firstName = this.currentUser.givenName
    this.familyName = this.currentUser.familyName
    this.phoneNumber = this.currentUser.phoneNumbers[0]
    this.loginName = this.currentUser.loginName
    this.isLoading = false
  }

  async updatePersonCredentialPassword(): Promise<void>{
    const payload: ChangeNewPasswordPayload = {
      personId: this.currentUser.id,
      password: this.password!.toString(),
    }

    try {
      this.saveInProgress = true
      await this.updatePersonCredentialPassById(payload)
      this.snackbarColor = "success"
      this.snackBarText = this.$t('profileDialog.savedPassword').toString()
      this.snackbar = true
    } catch (e) {
      this.snackbarColor = "error"
      this.snackBarText = this.$t('profileDialog.savePasswordErrorMessage').toString()
      this.snackbar = true
    } finally {
      this.saveInProgress = false
    }
  }

  mounted() {
    this.fetchData()
    this.selectedItem = this.currentLanguage

  }

}
</script>

<style lang="stylus" scoped>
.card-body
  min-height 200px
  overflow auto

.sloader
  width 100%

</style>
